import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { isAxiosError } from "axios";
import { useAuth } from "../context/AuthProvider";
import { ENABLE_KAKAO } from "../config/social";
import AuthFrame, { PasswordField } from "../components/design/AuthFrame";
import { authLink, authNext } from "../lib/authNavigation";
import { isRegistrationSessionKey } from "../lib/registrationDraft";
import { verificationRequestContext } from "../lib/verificationNavigation";

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");

function defaultDestination(account: { is_admin: boolean; is_merchant?: boolean; verification_exempt?: boolean } | null) {
  if (account?.is_admin) return "/admin/payment-requests";
  return account?.is_merchant || account?.verification_exempt ? "/pay" : "/dashboard";
}

export default function Login() {
  const nav = useNavigate();
  const location = useLocation();
  const { user, ready, login } = useAuth();
  const [email, setEmail] = useState(typeof location.state?.email === "string" ? location.state.email : "");
  const [pw, setPw] = useState("");
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const jumped = useRef(false);
  const submitting = useRef(false);
  const errorRef = useRef<HTMLDivElement>(null);

  const from = location.state?.from;
  const fromPath = typeof from === "string" ? from : from?.pathname ? `${from.pathname}${from.search || ""}${from.hash || ""}` : null;
  const next = authNext(new URLSearchParams(location.search).get("next")) || authNext(location.state?.next) || authNext(fromPath);
  const { amountEntry } = verificationRequestContext(next);
  const destination = next || defaultDestination(user);
  let backPath = "/";
  if (next) {
    const entry = new URL(next, window.location.origin);
    const sessionKey = entry.searchParams.get("session_key");
    if (entry.pathname === "/contracts/new" && entry.searchParams.get("entry") === "amount" && isRegistrationSessionKey(sessionKey)) {
      backPath = `/registration?session_key=${encodeURIComponent(sessionKey)}`;
    }
  }

  useEffect(() => {
    if (ready && user && !jumped.current) {
      jumped.current = true;
      nav(destination, { replace: true });
    }
  }, [ready, user, destination, nav]);
  useEffect(() => { if (err) errorRef.current?.focus(); }, [err]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting.current || !ready) return;
    if (!email.trim() || !pw) { setErr("이메일과 비밀번호를 입력해 주세요."); return; }
    submitting.current = true;
    setErr(null);
    setLoading(true);
    try {
      const me = await login(email.trim(), pw, remember);
      if (!jumped.current) {
        jumped.current = true;
        nav(next || defaultDestination(me), { replace: true });
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "";
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 429) setErr("로그인 요청이 많습니다. 잠시 후 다시 시도해 주세요.");
      else if (status === 401 || message.includes("401") || message.toLowerCase().includes("unauthorized")) setErr("이메일 또는 비밀번호를 다시 확인해 주세요.");
      else setErr("로그인하지 못했습니다. 잠시 후 다시 시도해 주세요.");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  }

  function handleKakaoLogin() {
    if (ENABLE_KAKAO && !loading) window.location.href = `${API_BASE}/api/auth/kakao/login?next=${encodeURIComponent(next || "/start")}`;
  }

  return <AuthFrame title="로그인" onBack={() => nav(backPath)} busy={loading} steps={amountEntry ? ["송금 금액", "정보·서류", "최종 확인"] : undefined} step={1} progressLabel="결제 요청">
    {next && <p className="ja-description">로그인 후 이어서 진행합니다.</p>}
    {err && <div ref={errorRef} tabIndex={-1} role="alert" className="ja-alert is-error">{err}</div>}
    <form className="ja-form" onSubmit={onSubmit} aria-busy={loading}>
      <div className="ja-field">
        <label htmlFor="login-email">이메일</label>
        <input id="login-email" type="email" required autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="가입한 이메일을 입력해 주세요" value={email} onChange={e => { setEmail(e.target.value); setErr(null); }} disabled={loading} />
      </div>
      <PasswordField id="login-password" label="비밀번호" required autoComplete="current-password" placeholder="비밀번호를 입력해 주세요" value={pw} onChange={e => { setPw(e.target.value); setErr(null); }} disabled={loading} />
      <div className="ja-login-options">
        <label className="ja-check"><input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} disabled={loading} />로그인 상태 유지</label>
        <Link className="ja-link" to={authLink("/find-account", next)}>아이디 · 비밀번호 찾기</Link>
      </div>
      <button className="ja-primary" type="submit" disabled={loading || !ready}>{loading ? "로그인 중…" : !ready ? "로그인 상태 확인 중…" : "로그인"}</button>
    </form>
    {ENABLE_KAKAO && <><div className="ja-divider">또는</div><button type="button" className="ja-secondary ja-kakao" onClick={handleKakaoLogin} disabled={loading || !ready}><MessageCircle size={19} />카카오로 계속하기</button></>}
    <p className="ja-switch">계정이 없나요?<Link className="ja-link" to={authLink("/signup", next)}>회원가입</Link></p>
  </AuthFrame>;
}
